import { forwardRef, useState, type HTMLAttributes, type MouseEvent } from 'react'
import { motion, type HTMLMotionProps } from 'framer-motion'
import { cn } from '@/lib/utils'

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  variant?: 'default' | 'glass' | 'outline'
  hover?: 'lift' | 'glow' | 'both' | 'spotlight' | 'none'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  as?: 'div' | 'article' | 'section'
  interactive?: boolean
}

const variantStyles = {
  default: 'bg-primary-mid/40 border border-white/5',
  glass: 'glass-card',
  outline: 'bg-transparent border border-white/10',
}

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      hover = 'none',
      padding = 'md',
      interactive = false,
      children,
      ...props
    },
    ref
  ) => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
    const [isHovered, setIsHovered] = useState(false)

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
      if (hover === 'spotlight' || interactive) {
        const rect = e.currentTarget.getBoundingClientRect()
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })
      }
    }

    const hoverAnimation = {
      lift: { y: -4, transition: { type: 'spring', stiffness: 400, damping: 30 } },
      glow: {
        boxShadow: '0 0 30px rgba(0, 217, 255, 0.3)',
        borderColor: 'rgba(0, 217, 255, 0.3)',
      },
      both: {
        y: -4,
        boxShadow: '0 0 30px rgba(0, 217, 255, 0.3)',
        borderColor: 'rgba(0, 217, 255, 0.3)',
      },
      spotlight: { y: -2 },
      none: {},
    }

    return (
      <motion.div
        ref={ref}
        className={cn(
          'relative rounded-2xl transition-colors duration-200 overflow-hidden',
          variantStyles[variant],
          paddingStyles[padding],
          (interactive || hover !== 'none') && 'cursor-pointer',
          className
        )}
        whileHover={hover !== 'none' ? hoverAnimation[hover] : undefined}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        {...(props as HTMLMotionProps<'div'>)}
      >
        {/* Spotlight effect overlay */}
        {(hover === 'spotlight' || interactive) && isHovered && (
          <motion.div
            className="absolute inset-0 pointer-events-none z-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              background: `radial-gradient(300px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0, 217, 255, 0.1), transparent 50%)`,
            }}
          />
        )}

        {/* Border glow effect */}
        {hover === 'both' && isHovered && (
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.1) 0%, rgba(123, 97, 255, 0.1) 100%)',
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </motion.div>
    )
  }
)

Card.displayName = 'Card'

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5', className)}
      {...props}
    />
  )
)

CardHeader.displayName = 'CardHeader'

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Tag = 'h3', ...props }, ref) => (
    <Tag
      ref={ref}
      className={cn(
        'text-lg font-semibold font-heading text-neutral-100',
        className
      )}
      {...props}
    />
  )
)

CardTitle.displayName = 'CardTitle'

interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-neutral-400', className)}
      {...props}
    />
  )
)

CardDescription.displayName = 'CardDescription'

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('', className)} {...props} />
  )
)

CardContent.displayName = 'CardContent'

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center pt-4', className)}
      {...props}
    />
  )
)

CardFooter.displayName = 'CardFooter'
